<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    disabled?: boolean;
    showConfirm?: boolean;
    showCancel?: boolean;
    confirmVariant?: "default" | "danger";
  }>(),
  {
    modelValue: undefined,
    disabled: false,
    showConfirm: true,
    showCancel: true,
    confirmVariant: "default",
  },
);
const emit = defineEmits(["update:confirm", "update:modelValue"]);

const confirmVariantClass = computed(() =>
  props.confirmVariant === "danger"
    ? "text-red-700 bg-red-500/10 hover:bg-red-500/20 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
    : "text-green-800 bg-ngreen/10 hover:bg-ngreen/40 hover:text-green-800 dark:text-ngreen dark:hover:text-white"
);

const handleAction = () => {
  if (props.disabled) {
    return;
  }
  emit("update:confirm", true);
};
</script>
<template>
  <AlertDialogRoot
    v-bind="
      typeof modelValue === 'boolean'
        ? { open: modelValue }
        : { defaultOpen: false }
    "
    @update:open="$emit('update:modelValue', $event)"
  >
    <!-- Only where there is something to click: an alert driven by `modelValue`
         is opened from the code that owns it and hands nothing to the trigger, so
         rendering one anyway left an empty button in the layout — a full-width
         line box among the session tools, and a tab stop that opens nothing. -->
    <AlertDialogTrigger v-if="$slots.default" v-bind="$attrs">
      <slot />
    </AlertDialogTrigger>
    <AlertDialogPortal>
      <Transition
        enter-from-class="opacity-0"
        enter-active-class="ease-out duration-300"
        enter-to-class="opacity-100"
        leave-from-class="opacity-100"
        leave-active-class="ease-out duration-200"
        leave-to-class="opacity-0"
      >
        <AlertDialogOverlay
          class="bg-midnight/60 backdrop-blur-sm data-[state=open]:animate-overlayShow fixed inset-0 z-[99]"
        />
      </Transition>
      <Transition
        enter-active-class="ease-out duration-300"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="ease-in duration-200"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <AlertDialogContent
          class="z-[99] text-[15px] data-[state=open]:animate-contentShow fixed top-[50%] left-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white dark:bg-midnight p-[25px] shadow-ui focus:outline-none"
        >
          <AlertDialogTitle class="text-mauve12 m-0 text-[17px] font-semibold">
            <slot name="title" />
          </AlertDialogTitle>
          <AlertDialogDescription
            class="text-mauve11 mt-4 mb-5 text-[15px] leading-normal"
          >
            <slot name="content" />
          </AlertDialogDescription>
          <div class="flex justify-end gap-2 md:gap-[15px]">
            <AlertDialogCancel
              v-if="showCancel"
              class="text-black/60 dark:text-white/60 bg-black/5 dark:bg-white/5 dark:hover:bg-white/10 dark:hover:text-white/70 hover:bg-black/10 hover:text-black/70 inline-flex h-[35px] items-center justify-center rounded-[4px] text-xs md:text-sm px-2 md:px-[15px] font-semibold leading-none outline-none"
            >
              <slot name="cancel">Cancel</slot>
            </AlertDialogCancel>
            <AlertDialogAction
              v-if="showConfirm"
              :class="confirmVariantClass"
              class="focus:shadow-red-500 inline-flex h-[35px] items-center justify-center rounded-[4px] text-xs md:text-sm px-2 md:px-[15px] font-semibold leading-none outline-none focus:shadow-[0_0_0_2px]"
              :disabled="props.disabled"
              @click="handleAction"
            >
              <slot name="confirm">Confirm</slot>
            </AlertDialogAction>
            <slot name="actions"></slot>
          </div>
        </AlertDialogContent>
      </Transition>
    </AlertDialogPortal>
  </AlertDialogRoot>
</template>

<style scoped></style>
